import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { ActionType, DisableReason } from '@common/enums/command-hub/command-buttons';
import DashboardCommandButton from '@common/components/CommandHub/DashboardCommandButton';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MessageType from '@common/enums/robot/rsw/message-types';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import {
  ActionStatus,
  CleaningCommandButtonSender,
} from '@common/interfaces/robot/buttons/command-buttons';
import RswState from '@common/enums/robot/rsw/rsw-state';
import { navigators } from '@common/interfaces/robot/navigator';
import { sendRobotWebsocketMessage } from 'store/middleware/robot-websocket-middleware';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectMissions, selectRooms } from '@common/store/room/room-selectors';
import { NavigatorAvailability } from '@common/enums/command-hub/navigator';
import { selectIsAdvancedModeEnabled } from '@common/store/app-configurations/app-configurations-selectors';
import { getCurrentPageName, trackEvent } from 'services/analytics';
import { AnalyticsEvent } from 'services/analytics-events';

const RobotCommandButtons: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const missions = useSharedSelector((state) => selectMissions(state));
  const rooms = useSharedSelector((state) => selectRooms(state));
  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));

  const robot = robotsData.currentRobot;

  if (robot == null) {
    return null;
  }

  if (robot == null || robot.status == null) return null;

  const getSortedFilteredActions = (actions: ActionStatus[]): ActionStatus[] => {
    const priority: ActionType[] = [
      ActionType.ConfirmMaintenance,
      ActionType.Clean,
      ActionType.Pause,
      ActionType.Resume,
      ActionType.WakeUp,
      ActionType.ActivateSchedule,
      ActionType.Abort,
      ActionType.Home,
    ];

    const priorityMap = new Map<ActionType, number>(priority.map((a, idx) => [a, idx]));

    return (
      actions
        // filter out cleaning if disabled because paused or homing
        .filter(
          (a) =>
            priority.includes(a.action) &&
            !(
              a.action === ActionType.Clean &&
              !a.isEnabled &&
              a.disableReason != null &&
              [DisableReason.Paused, DisableReason.Homing].includes(a.disableReason)
            ),
        )
        // sort by priority, preserving original order for non-prioritized items
        .map((item, index) => ({ ...item, originalIndex: index }))
        .sort((a, b) => {
          const aRank = priorityMap.get(a.action) ?? priority.length + a.originalIndex;
          const bRank = priorityMap.get(b.action) ?? priority.length + b.originalIndex;
          return aRank - bRank;
        })
        .map(({ originalIndex, ...rest }) => rest)
    );
  };

  const robotActions = getSortedFilteredActions(
    robotsData.currentRobotData.actionsList?.actionList ?? [],
  );

  const filteredAvailableNavigators = [
    ...navigators.filter((n) => n.availability === NavigatorAvailability.Public),
  ];

  if (isAdvancedModeEnabled) {
    filteredAvailableNavigators.push(
      ...[...navigators.filter((n) => n.availability === NavigatorAvailability.AdvancedModeOnly)],
    );
  }

  const handleCommandButtonClick = (cleaningCommandMsg: CleaningCommandButtonSender) => {
    trackEvent(AnalyticsEvent.RobotCommandRequested, {
      robot_id: robot.id,
      robot_serial: robot.serialNumber,
      command_action: cleaningCommandMsg.action,
      source_page: getCurrentPageName() ?? 'unknown',
    });
    if (cleaningCommandMsg.action === ActionType.ConfirmMaintenance) {
      dispatch(
        sendRobotWebsocketMessage({
          type: MessageType.Actions,
          message: { action: ActionType.ConfirmMaintenance, robotStatus: robot.status?.state },
        }),
      );
    } else {
      dispatch(
        sendRobotWebsocketMessage({
          type: MessageType.Actions,
          message: cleaningCommandMsg,
        }),
      );
    }
  };

  return (
    <Grid item container xs={12} gap={1}>
      {/* default buttons */}
      <Grid item container xs={12} gap={1}>
        {robotActions.length === 0 ? (
          <Alert
            icon={<CircularProgress size="22px" color="primary" />}
            color="info"
            sx={{ width: '100%' }}
          >
            {t('loadinginfo-dashboardcommandbuttons')}
          </Alert>
        ) : (
          robotActions.map((as) => (
            <DashboardCommandButton
              key={as.action}
              currentRobotState={robot.status?.state ?? RswState.Unknown}
              actionStatus={as}
              currentRoomId={robot.status?.roomId}
              awaitingResponse={
                robotsData.rest.getRobotInformationStatus === HttpFetchStatus.Loading
              }
              availableRooms={rooms}
              onCommandButtonClick={handleCommandButtonClick}
              availableNavigators={filteredAvailableNavigators}
              missions={missions}
              abortedMissionId={null}
            />
          ))
        )}
      </Grid>
    </Grid>
  );
};

export default RobotCommandButtons;
